import React, {
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  SyntheticEvent,
} from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { UseInfiniteQueryResult } from 'react-query';
import classNames from 'classnames';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  infiniteScrollContainer: {
    maxHeight: '100%',
    overflowY: 'auto',
    flex: 1,
    padding: [[0, theme.marginBase * 2]],
  },
}));

interface Props<TData> {
  children: React.ReactNode;
  infiniteQueryResponse?: UseInfiniteQueryResult<TData>;
  loader?: React.ReactNode;
  className?: string;
  intersectOnWindow?: boolean;
}

export const InfiniteLoadingContainer = forwardRef(
  <TData extends {}>(
    {
      children,
      infiniteQueryResponse,
      loader,
      intersectOnWindow,
      className,
      ...rest
    }: Props<TData>,
    ref: any,
  ) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme });
    const fetching = useRef(false);

    const fetchNextPage =
      infiniteQueryResponse && infiniteQueryResponse.fetchNextPage;

    useEffect(() => {
      if (infiniteQueryResponse && !infiniteQueryResponse.isFetching) {
        fetching.current = false;
      }
    });

    const onScroll = useCallback(
      (e: SyntheticEvent<HTMLDivElement>) => {
        if (!fetchNextPage || fetching.current) {
          return;
        }
        const currentScroll =
          e.currentTarget.offsetHeight + e.currentTarget.scrollTop;
        const offset = e.currentTarget.offsetHeight;

        if (currentScroll > e.currentTarget.scrollHeight - offset) {
          fetching.current = true;
          fetchNextPage();
        }
      },
      [fetchNextPage],
    );

    return (
      <div
        className={classNames(classes.infiniteScrollContainer, className)}
        {...rest}
        ref={ref}
        onScroll={onScroll}
      >
        {children}
        {infiniteQueryResponse && infiniteQueryResponse.hasNextPage && loader}
      </div>
    );
  },
);
